import React, { FC, useEffect, useMemo, useState } from 'react';
import './edit-standard-connection-name-modal.scss';
import GenericModal from '../../../../shared/generic-modal/generic-modal';
import { CustomInput } from 'src/components/shared/custom-input/custom-input';
import FormField from '../../../../shared/edit-row-modal/form-field/form-field';
import {
    ILLEGAL_CHARACTERS_MESSAGE,
    validateString
} from '../../../../../utils/validationUtils';
import { ConnectionModel } from '../../connection.model';
import { useStores } from '../../../../store';
import usePromiseFeedback from '../../../../shared/promise-feedback/usePromiseFeedback';

interface EditStandardConnectionNameModalProps {
    visible: boolean;
    onClose: () => void;
    connection: ConnectionModel;
}

const EditStandardConnectionNameModal: FC<EditStandardConnectionNameModalProps> = ({ visible, onClose, connection }) => {
    const [name, setName] = useState(connection.connectionName || connection.circuitId || '');
    const [isLoading, setIsLoading] = useState(false);
    const { customerStore } = useStores();
    const [promiseFeedbackFactory] = usePromiseFeedback();

    // Sync name state with connection prop changes
    useEffect(() => {
        if (visible) {
            setName(connection.connectionName || connection.circuitId || '');
        }
    }, [visible]);

    const generateError = (value: string) => {
        return !value || !validateString(value, false, ['@', '#', '$', '%', '&', '*', '+', '/']) ? '' : ILLEGAL_CHARACTERS_MESSAGE;
    };
    const nameError = useMemo(() => generateError(name), [name]);
    const hasError = !!nameError;
    const isDirty = name !== (connection.connectionName || connection.circuitId || '');

    const handleConfirm = () => {
        setIsLoading(true);
        promiseFeedbackFactory({
            promise: customerStore.setConnectionName(connection.id, name),
            notifObj: { show: true, notificationMsg: 'Connection name updated successfully' },
            errorCallback: () => setIsLoading(false),
            successCallback: () => {
                setIsLoading(false);
                onClose();
            }
        });
    };

    return (
        <GenericModal
            open={visible}
            title="Edit Connection Name"
            wrapClassName="edit-standard-connection-name-modal"
            closable={false}
            width={562}
            body={
                <div className="modal-body">
                    <FormField errorMessage={nameError}>
                        <CustomInput
                            inputType="INPUT"
                            className="connection-name"
                            hideWarnMessage={!!nameError}
                            value={name}
                            placeholder="Enter connection name"
                            onChange={e => setName(e.target.value)}
                            maxLength={25}
                        />
                    </FormField>
                </div>
            }
            actionButtons={[
                { text: 'Cancel', onClick: onClose, className: 'secondary-btn' },
                {
                    text: 'Save',
                    onClick: () => handleConfirm(),
                    disabled: !isDirty || hasError,
                    className: `primary-btn${isLoading ? ' loading' : ''}`
                }
            ]}
            onCancel={onClose}
        />
    );
};

export default EditStandardConnectionNameModal;
